import {
  createMutationStore,
  createQueryStore,
  useQueryClient,
} from '@ferreri/minato-react-utils';
import { alert } from '@ferreri/prever-component-lib';
import { useRouter } from 'next/router';

import {
  deletePlanBenefitConsentId,
  deletePlanBenefitItemDetailsId,
  getBenefitCategories,
  getPaginatedPlanBenefits,
  getPlanBenefit,
  getPlanBenefitConsentId,
  getPlanBenefitConsents,
  postPlanBenefit,
  postPlanBenefitConsent,
  postPlanBenefitItemDetails,
  putPlanBenefit,
  putPlanBenefitConsent,
  putPlanBenefitItemDetails,
} from './plan-benefit.client';
import {
  Benefit,
  BenefitCategory,
  BenefitConsentData,
  BenefitItemDetailsData,
  BenefitUpdateConsentData,
  BenefitUpdateItemDetailsData,
  PlanBenefitData,
  PlanBenefitEdit,
} from './plan-benefit.types';

import { useMemo } from 'react';

const BENEFITS_PAGE_SIZE = 12;
const ALL_BENEFITS_PER_PAGE = 1000;

export const useAllBenefits = () => {
  return createQueryStore({
    queryKey: ['plan_benefits', 'all'],
    queryFn: () =>
      getPaginatedPlanBenefits({ per_page: ALL_BENEFITS_PER_PAGE }).then(
        (response) => response.benefits,
      ),
    refetchOnWindowFocus: false,
  });
};

export const usePaginatedBenefits = (page: number) => {
  return createQueryStore({
    queryKey: ['plan_benefits', 'paginated', page],
    queryFn: () =>
      getPaginatedPlanBenefits({ page, per_page: BENEFITS_PAGE_SIZE }),
    refetchOnWindowFocus: false,
  });
};

export const useBenefitCategories = () => {
  const { data, isLoading } = createQueryStore({
    queryKey: ['benefit_categories'],
    queryFn: getBenefitCategories,
    refetchOnWindowFocus: false,
  });

  const options = useMemo(
    () =>
      data?.map((benefitCategory: BenefitCategory) => ({
        label: benefitCategory.name,
        value: benefitCategory.id,
      })),
    [data],
  );

  return { data, options, isLoading };
};

export const usePlanBenefitIdRouteParam = (): string => {
  const router = useRouter();

  return router.query?.plan_benefit_id as string;
};

const usePlanBenefitConsentIdRouteParam = (): string => {
  const router = useRouter();

  return router.query?.consent_id as string;
};

const usePlanBenefitItemDetailsIdRouteParam = (): string => {
  const router = useRouter();

  return router.query?.item_id as string;
};

export const usePlanBenefit = (id: number) => {
  return createQueryStore({
    queryKey: ['plan_benefit', id],
    queryFn: () => getPlanBenefit(id),
    enabled: Boolean(id),
  });
};

export const useBenefitConsents = (benefit_id: number) => {
  return createQueryStore({
    queryKey: ['plan_benefits_consent', benefit_id],
    queryFn: () => getPlanBenefitConsents(benefit_id),
    enabled: Boolean(benefit_id),
  });
};

export const useBenefitConsentId = (id: number) => {
  return createQueryStore({
    queryKey: ['plan_benefits_consent_id', id],
    queryFn: () => getPlanBenefitConsentId(id),
    enabled: Boolean(id),
  });
};

export const useBenefitItemDetailsId = (id: number) => {
  return createQueryStore({
    queryKey: ['plan_benefits_item_details_id', id],
    queryFn: () => getPlanBenefitConsentId(id),
    enabled: Boolean(id),
  });
};

export const useCurrentPlanBenefitConsentId = () => {
  const planBenefitConsentId = usePlanBenefitConsentIdRouteParam();

  return useBenefitConsentId(Number(planBenefitConsentId));
};

export const useCurrentPlanBenefitItemDetailsId = () => {
  const planBenefitItemDetailsId = usePlanBenefitItemDetailsIdRouteParam();

  return Number(planBenefitItemDetailsId);
};

export const useCurrentPlanBenefit = () => {
  const planBenefitId = usePlanBenefitIdRouteParam();

  return usePlanBenefit(Number(planBenefitId));
};

export const useUpdatePlanBenefit = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: (data: PlanBenefitEdit) => putPlanBenefit(data),
    onSuccess: (variables) => {
      queryClient.invalidateQueries(['plan_benefits']);
      queryClient.invalidateQueries(['plan_benefit', variables.id]);

      router.back();
    },
    onError: () => {
      alert.helper(
        'Erro ao atualizar distribuição',
        'Os dados da distribuição estão incorretos. Por favor, preencha os campos novamente.',
      );
    },
  });
};

export const useCreatePlanBenefit = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: (data: PlanBenefitData) => postPlanBenefit(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['plan_benefits']);

      router.push('/admin/plan-benefits');
    },
    onError: () => {
      alert.helper(
        'Erro ao criar benefício',
        'Os dados do benefício estão incorretos. Por favor, preencha os campos novamente.',
      );
    },
  });
};

export const usePlanBenefitsOptions = () => {
  const planBenefitsQuery = useAllBenefits();
  const options = useMemo(
    () =>
      planBenefitsQuery.data?.map((benefit: Benefit) => ({
        label: benefit.name,
        value: benefit.id,
      })),
    [planBenefitsQuery.data],
  );

  return { ...planBenefitsQuery, data: options };
};

export const useCreateBenefitItemDetails = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: (data: BenefitItemDetailsData) =>
      postPlanBenefitItemDetails(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['plan_benefits_item_details']);
      queryClient.invalidateQueries(['plan_benefit']);
      router.back();
    },
  });
};

export const useCreateBenefitConsent = () => {
  const router = useRouter();
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: (data: BenefitConsentData) => postPlanBenefitConsent(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['plan_benefits_consent']);

      router.back();
    },
  });
};

export const useUpdateBenefitConsent = () => {
  const router = useRouter();
  const { data: planBenefitConsent } = useCurrentPlanBenefitConsentId();
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: (data: BenefitUpdateConsentData) => {
      const payload = {
        ...data,
        id: planBenefitConsent?.id,
      };
      return putPlanBenefitConsent(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['plan_benefits_consent']);
      queryClient.invalidateQueries(['plan_benefits_consent_id']);

      router.back();
    },
  });
};

export const useDeletePlanBenefitConsentId = () => {
  return createMutationStore({
    mutationFn: deletePlanBenefitConsentId,
  });
};

export const useUpdateBenefitItemDetails = () => {
  const router = useRouter();
  const id = useCurrentPlanBenefitItemDetailsId();
  const queryClient = useQueryClient();

  return createMutationStore({
    mutationFn: (data: BenefitUpdateItemDetailsData) => {
      const payload = {
        ...data,
        id: id,
      };
      return putPlanBenefitItemDetails(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['plan_benefits']);
      queryClient.invalidateQueries(['plan_benefits_item_details']);
      queryClient.invalidateQueries(['plan_benefit']);

      router.back();
    },
  });
};

export const useDeletePlanBenefitItemDetailsId = () => {
  return createMutationStore({
    mutationFn: deletePlanBenefitItemDetailsId,
  });
};

export const useCurrentBenefitItemDetails = () => {
  const currentBenefitId = usePlanBenefitIdRouteParam();
  const { data, isLoading } = usePlanBenefit(Number(currentBenefitId));
  const currentItemDetailsId = useCurrentPlanBenefitItemDetailsId();

  const detail = data?.details?.find(
    (details_id) => details_id?.id === currentItemDetailsId,
  );

  return { detail: detail ?? null, isLoading };
};

export const useCurrentPlanBenefitWithGroupCategory = () => {
  const { data: planBenefit, isLoading: isLoadingBenefit } =
    useCurrentPlanBenefit();
  const { data: categories, isLoading: isLoadingCategories } =
    useBenefitCategories();

  const isLoading = isLoadingBenefit || isLoadingCategories;

  const benefitWithGroupCategory = useMemo(() => {
    if (!planBenefit || !categories) return null;

    const category = categories.find(
      (cat) => Number(cat.id) === Number(planBenefit.benefit_category_id),
    );

    return {
      ...planBenefit,
      benefit_group_name: category?.benefit_category_group?.name ?? null,
    };
  }, [planBenefit, categories]);

  return {
    data: benefitWithGroupCategory,
    isLoading,
  };
};
